import { createReducer } from '@reduxjs/toolkit';
import _entries from 'lodash/entries';
import _flatMap from 'lodash/fp/flatMap';
import _pipe from 'lodash/fp/pipe';
import _isEmpty from 'lodash/isEmpty';
import _values from 'lodash/values';
import { clearAllProjectData } from '../actions/clearAllProjectData';
import factsForDocumentUpdated from '../actions/factsForDocumentUpdated';
import resetCalculationDataForDocument from '../actions/resetCalculationDataForDocument';
import { sumIfNumber } from '../utilities/calculationUtilities';
import {
  CURRENT_ASSETS_CATEGORY,
  CURRENT_ASSETS_KEY,
  CURRENT_LIABILITIES_CATEGORY,
  CURRENT_LIABILITIES_KEY,
  EBITDA_FLAG,
  REPORTED_EBITDA_KEY,
  REPORTED_REVENUE_KEY,
  REVENUE_CATEGORY,
} from '../utilities/constants';
import { deconstructFactKey } from '../utilities/keyUtilities';

const createFactDataPoint = (fact) => {
  return _entries(fact).map((entry) => ({
    compositeKey: entry[0],
    amount: entry[1],
  }));
};

export const extractFactLineItems = (updatedFactsForDocument) => {
  const factLineItems = _pipe(
    _values,
    _flatMap(createFactDataPoint)
  )(updatedFactsForDocument);

  return factLineItems;
};

const altersReportedRevenue = (fact) => {
  const { aggregator } = deconstructFactKey(fact.compositeKey);
  return aggregator === REVENUE_CATEGORY;
};

const altersCurrentAssets = (fact) => {
  const { aggregator } = deconstructFactKey(fact.compositeKey);
  return (
    (aggregator || '').toLowerCase() === CURRENT_ASSETS_CATEGORY.toLowerCase()
  );
};

const altersCurrentLiabilities = (fact) => {
  const { aggregator } = deconstructFactKey(fact.compositeKey);
  return (
    aggregator && aggregator.toLowerCase() === CURRENT_LIABILITIES_CATEGORY
  );
};

const altersReportedEbitda = (fact) => {
  const { ebitdaFlag } = deconstructFactKey(fact.compositeKey);
  return ebitdaFlag === EBITDA_FLAG;
};

const accumulateForPredicate = (factLineItems, predicate, targetKey) => {
  const accumulation = factLineItems
    .filter(predicate)
    .reduce((accumulator, fact) => {
      const { timePeriod } = deconstructFactKey(fact.compositeKey);
      const { amount } = fact;
      const previousTotal = accumulator[timePeriod] || 0;
      accumulator[timePeriod] = sumIfNumber(previousTotal, amount);
      return accumulator;
    }, {});

  if (_isEmpty(accumulation)) {
    return {};
  }

  return { [targetKey]: accumulation };
};

const accumulateReportedRevenue = (factLineItems) =>
  accumulateForPredicate(
    factLineItems,
    altersReportedRevenue,
    REPORTED_REVENUE_KEY
  );

const accumulateCurrentAssets = (factLineItems) =>
  accumulateForPredicate(
    factLineItems,
    altersCurrentAssets,
    CURRENT_ASSETS_KEY
  );

const accumulateCurrentLiabilities = (factLineItems) =>
  accumulateForPredicate(
    factLineItems,
    altersCurrentLiabilities,
    CURRENT_LIABILITIES_KEY
  );

const accumulateReportedEbitda = (factLineItems) =>
  accumulateForPredicate(
    factLineItems,
    altersReportedEbitda,
    REPORTED_EBITDA_KEY
  );

const isInvalidPayload = (payload) => {
  const invalid =
    payload === undefined ||
    payload.documentCode === undefined ||
    payload.facts === undefined;

  return invalid;
};

const factCalculations = createReducer(
  {},
  {
    [factsForDocumentUpdated]: (state, action) => {
      if (isInvalidPayload(action.payload)) {
        return state;
      }

      const { facts } = action.payload;
      const factLineItems = extractFactLineItems(facts);
      const reportedRevenue = accumulateReportedRevenue(factLineItems);
      const reportedEbitda = accumulateReportedEbitda(factLineItems);
      const currentAssets = accumulateCurrentAssets(factLineItems);
      const currentLiabilities = accumulateCurrentLiabilities(factLineItems);

      return {
        ...state,
        ...reportedRevenue,
        ...reportedEbitda,
        ...currentAssets,
        ...currentLiabilities,
      };
    },
    /**
     * Because fact is computed from inputs that may span multiple documents, it is not possible to clear a fact by document code at this point.
     */
    [resetCalculationDataForDocument]: () => ({}),
    [clearAllProjectData]: () => ({}),
  }
);

export default factCalculations;
